import { AxiosResponse } from "axios";
import { ApiError } from "./api.service";

export const promiseWithErrorHandling = async <T = unknown>(
  serviceFunction: () => Promise<T>,
  reject: (value: unknown) => void,
) => {
  try {
    return await serviceFunction();
  } catch (error) {
    if (error instanceof ApiError) {
      return reject((error as ApiError).message);
    }
    if (error instanceof Error) {
      return reject(error.message || "An unexpected error occurred");
    }
    return reject("An unexpected error occurred");
  }
};

export const requestWithErrorHandling = async (
  serviceFunction: () => Promise<AxiosResponse>,
) => {
  const response = await serviceFunction();

  if (!response.data && !response?.status.toString().startsWith("2")) {
    throw new ApiError(500, "Server returned an unexpected response");
  }

  if (!response?.status.toString().startsWith("2")) {
    throw new ApiError(
      response.status,
      response.data.message ||
      response.statusText ||
      "An unexpected error occurred",
    );
  } else {
    return response.data;
  }
};

/** Axios-style error with response.data (backend message). */
function getMessageFromAxiosError(error: any): string | null {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return null;
  if (typeof data.message === "string" && data.message) return data.message;
  if (typeof data.error === "string" && data.error) return data.error;
  if (Array.isArray(data.errors) && data.errors[0]) {
    const first = data.errors[0];
    return typeof first === "string" ? first : first?.message || first?.msg || null;
  }
  return null;
}

export const handleRejectError = (
  reject: (value: any) => any,
  error: unknown,
) => {
  if (error instanceof ApiError) {
    // Create an error object that includes both message and status code
    const errorObj = {
      message: (error as ApiError).message,
      statusCode: (error as ApiError).statusCode
    };
    return reject(errorObj);
  }
  const backendMessage = getMessageFromAxiosError(error as any);
  if (backendMessage) {
    const axiosError = error as any;
    const errorObj = {
      message: backendMessage,
      statusCode: axiosError?.response?.status || 500
    };
    return reject(errorObj);
  }
  if (error instanceof Error) {
    const axiosError = error as any;
    const errorObj = {
      message: error.message || "An unexpected error occurred",
      statusCode: axiosError?.response?.status || 500
    };
    return reject(errorObj);
  }
  return reject({
    message: "An unexpected error occurred",
    statusCode: 500
  });
};
